import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { API, auth, hasRole, riyadhTodayStr, riyadhDaysAgoStr, FINANCE_ROLES, HR_ROLES, MANAGE_ROLES } from './reports/shared';
import Icon from '../../components/dashboard/DashboardIcons';
import FilterBar from './reports/FilterBar';
import ExecutiveTab from './reports/tabs/ExecutiveTab';
import SportsTab from './reports/tabs/SportsTab';
import HRTab from './reports/tabs/HRTab';
import FinancialTab from './reports/tabs/FinancialTab';
import CoachingTab from './reports/tabs/CoachingTab';
import GovernanceTab from './reports/tabs/GovernanceTab';
import ReportStudio from '../../components/reportStudio/ReportStudio';

const TABS = [
  { key: 'executive', label: 'النظرة التنفيذية', icon: 'grid', allowed: () => true, showDate: false },
  { key: 'sports', label: 'التقارير الرياضية المتقدمة', icon: 'trophy', allowed: () => true, showDate: false },
  { key: 'hr', label: 'الموارد البشرية', icon: 'briefcase', allowed: (u) => hasRole(u, HR_ROLES), showDate: true },
  { key: 'financial', label: 'التقارير المالية', icon: 'banknotes', allowed: (u) => hasRole(u, FINANCE_ROLES), showDate: true },
  { key: 'coaching', label: 'المدربون والعمل الفني', icon: 'whistle', allowed: (u) => hasRole(u, MANAGE_ROLES), showDate: true },
  { key: 'governance', label: 'الحوكمة والإدارة', icon: 'flag', allowed: (u) => hasRole(u, MANAGE_ROLES), showDate: true },
  { key: 'studio', label: 'استوديو التقارير', icon: 'document', allowed: () => true, showDate: false },
];

const defaultDraft = () => ({ date_from: riyadhDaysAgoStr(30), date_to: riyadhTodayStr(), team_id: '', category_id: '' });

export default function ReportsPage() {
  const { user, club, selectedSeasonId, seasons, setSelectedSeasonId } = useOutletContext() || {};
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get('tab') || 'executive';
  const tabMeta = TABS.find((t) => t.key === requestedTab) || TABS[0];
  const activeTab = tabMeta.key;
  const deniedTab = !tabMeta.allowed(user);

  const visibleTabs = useMemo(() => TABS.filter((t) => t.allowed(user)), [user]);
  const [draft, setDraft] = useState(defaultDraft());
  const [filters, setFilters] = useState(defaultDraft());
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const g = (url) => axios.get(`${API}${url}`, auth()).then((r) => r.data).catch(() => []);
    Promise.all([g('/teams'), g('/categories')]).then(([t, c]) => {
      setTeams(Array.isArray(t) ? t : []);
      setCategories(Array.isArray(c) ? c : []);
    });
  }, []);

  const changeTab = (key) => setParams({ tab: key });
  const applyFilters = () => setFilters(draft);
  const resetFilters = () => { const d = defaultDraft(); setDraft(d); setFilters(d); };

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="reports-center-page">
      <div>
        <h2 className="text-lg font-bold text-slate-900" data-testid="reports-center-title">مركز التقارير والتحليلات</h2>
        <p className="text-sm text-slate-500">رؤية شاملة لأداء منشأتك الرياضي والإداري والمالي من مكان واحد.</p>
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="reports-tabs">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => changeTab(t.key)}
            data-testid={`reports-tab-${t.key}`}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${activeTab === t.key ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Icon name={t.icon} className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {deniedTab ? (
        <div className="rounded-xl border border-red-200 bg-red-50 py-10 text-center text-sm text-red-700" data-testid="reports-tab-denied">
          لا تملك صلاحية الوصول لهذا التقرير
        </div>
      ) : (
        <>
          {tabMeta.showDate && (
            <FilterBar draft={draft} setDraft={setDraft} onApply={applyFilters} onReset={resetFilters} showTeamCategory={tabMeta.showTeamCategory} teams={teams} categories={categories} />
          )}
          {activeTab === 'executive' && <ExecutiveTab user={user} club={club} />}
          {activeTab === 'sports' && <SportsTab filters={filters} club={club} seasonId={selectedSeasonId} seasons={seasons} onSeasonChange={setSelectedSeasonId} />}
          {activeTab === 'hr' && <HRTab filters={filters} club={club} seasonId={selectedSeasonId} />}
          {activeTab === 'financial' && <FinancialTab filters={filters} club={club} seasonId={selectedSeasonId} />}
          {activeTab === 'coaching' && <CoachingTab filters={filters} club={club} />}
          {activeTab === 'governance' && <GovernanceTab filters={filters} club={club} />}
          {activeTab === 'studio' && <ReportStudio />}
        </>
      )}
    </div>
  );
}
